import { useState, useEffect, useRef } from 'react';
import { useCart } from '../../hooks/useCart';
import { Link } from 'react-router-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getProductImage } from '../../utils/imageStorage';
import { getVisibleProducts, isExclusivePreviewActive, EXCLUSIVE_PREVIEW_END, isProductVisibleToPublic } from '../../utils/exclusivePreview';
import NewArrivalsSection from '@/components/feature/NewArrivalsSection';

export default function Boutique() {
  const { addItem } = useCart();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const showDebug = searchParams.get('debug') === '1';
  const notificationTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isMountedRef = useRef(true);
  const [productImages, setProductImages] = useState<{[key: string]: string}>({});
  const [imagesLoaded, setImagesLoaded] = useState(false);

  // Charger les images personnalisées au démarrage
  useEffect(() => {
    isMountedRef.current = true;
    
    const loadAllImages = async () => {
      try {
        const allProducts = [
          'ange-reve-doux',
          'ange-serenite',
          'ange-a-poser-modele-1',
          'ange-a-poser-modele-2',
          'ange-a-poser-modele-3',
          'ange-or-lumiere',
          'lot-3-anges-reve-doux',
          'lot-3-anges-serenite',
          'lot-3-anges-mixte',
          'bougie-avec-led-v2',
          'bougie-livre-plie-edition-noel',
          'bougie-livre-plie-dentelle-or',
          'chandelier-livre-plie-dentelle-perles',
          'chandelier-livre-plie-blanc-perles',
          'sapin-noel-livre-plie',
          'sapin-lumineux-dore',
          'sapin-or-lumieres',
          'sapin-flamme-doree-lumieres',
          'boule-noel-argent-pailletee',
          'boule-noel-pages-livre',
          'boule-noel-livres-plies-bleu',
          'boule-noel-papier-rouge-naturel',
          'boule-noel-papier-bleu-pois-dores',
          'boule-noel-papier-gris-argent',
          'decoration-murale-livre-sculpte-noel',
          'decoration-murale-feuillage-ornements-noel',
          'bonhomme-neige-livre-plie',
          'bonhomme-neige-lumineux',
          'cage-oiseaux-oiseau-fleurs',
          'cage-oiseaux-trio-oiseaux-fleur-rouge',
          'jesus-portrait-livre-plie',
          'coeur-blanc-livre-plie'
        ];

        const loadedImages: {[key: string]: string} = {};
        
        for (const productId of allProducts) {
          const image = await getProductImage(productId);
          if (image) {
            loadedImages[productId] = image;
          }
        }
        
        if (isMountedRef.current) {
          setProductImages(loadedImages);
          setImagesLoaded(true);
        }
      } catch (error) {
        console.error('Erreur lors du chargement des images:', error);
        if (isMountedRef.current) {
          setImagesLoaded(true);
        }
      }
    };
    
    loadAllImages();
    
    return () => {
      isMountedRef.current = false;
      if (notificationTimeoutRef.current) {
        clearTimeout(notificationTimeoutRef.current);
        notificationTimeoutRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    // SEO JSON-LD pour la page boutique
    const jsonLd = {
      "@context": "https://schema.org",
      "@type": "Store",
      "name": "Les Carnets de Lolotte - Boutique",
      "description": "Boutique artisanale Les Carnets de Lolotte. Créations uniques en papier plié : anges de Noël, bougies décoratives, sapins artisanaux. Fait main avec amour en France. Livraison soignée.",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/boutique`,
      "image": "https://static.readdy.ai/image/fd456b0d540e8f517714341c204b3126/e5a40b4f7b52fcfa548a8f38ffe25152.png",
      "priceRange": "€€",
      "currenciesAccepted": "EUR",
      "paymentAccepted": "Credit Card, Debit Card",
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "Créations artisanales en papier plié",
        "itemListElement": [
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Product",
              "name": "Anges de Noël en papier plié",
              "description": "Anges décoratifs faits main en papier plié"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Product",
              "name": "Bougies décoratives en livre plié",
              "description": "Bougies artisanales créées à partir de livres pliés"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Product",
              "name": "Sapins de Noël artisanaux",
              "description": "Sapins décoratifs en papier plié fait main"
            }
          }
        ]
      }
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(jsonLd);
    document.head.appendChild(script);

    // Mise à jour des meta tags
    document.title = "Les Carnets de Lolotte – Boutique artisanale décorations en livre plié";
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Boutique artisanale Les Carnets de Lolotte. Créations uniques en papier plié : anges de Noël, bougies décoratives, sapins artisanaux. Fait main avec amour en France. Livraison soignée.');
    }

    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'décoration Noël artisanale, ange papier plié, bougie livre plié, sapin Noël fait main, création artisanale France, Les Carnets de Lolotte');
    }

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const products = [
    // Anges de Noël
    {
      id: 'ange-reve-doux',
      name: 'Ange "Rêve Doux"',
      price: 4,
      image: productImages['ange-reve-doux'] || 'https://public.readdy.ai/ai/img_res/5d6e1851-3bb6-4061-970b-fd2625a28c0d.png',
      category: 'Collection de Noël',
      subcategory: 'Anges de Noël',
      description: 'Fait main à partir de livres anciens',
      link: '/produit-ange-reve-doux'
    },
    {
      id: 'ange-serenite',
      name: 'Ange "Sérénité"',
      price: 4,
      category: 'Collection de Noël',
      subcategory: 'Anges de Noël',
      description: 'Fait main à partir de livres anciens',
      image: productImages['ange-serenite'] || 'https://public.readdy.ai/ai/img_res/744b7b75-1b4f-49c2-a689-d5bb175d844e.png',
      link: '/produit-ange-serenite'
    },
    {
      id: 'ange-a-poser-modele-1',
      name: 'Ange à poser – 25 cm',
      price: 30,
      category: 'Collection de Noël',
      subcategory: 'Anges de Noël',
      description: 'Ange fabriqué à la main en pliage de livre. Chaque pièce est unique et poétique, parfaite pour un décor de Noël chaleureux.',
      image: productImages['ange-a-poser-modele-1'] || 'https://static.readdy.ai/image/fd456b0d540e8f517714341c204b3126/746da2ef76b6c797f8ba009386f5cf90.png',
      link: '/produit-ange-a-poser-modele-1',
      badge: 'Nouveau ✨',
      badgeColor: 'bg-[#C49B86] text-white',
      isNew: true
    },
    {
      id: 'ange-a-poser-modele-2',
      name: 'Ange à poser – 25 cm',
      price: 30,
      category: 'Collection de Noël',
      subcategory: 'Anges de Noël',
      description: 'Création artisanale en pages pliées, entièrement réalisée à la main.',
      image: productImages['ange-a-poser-modele-2'] || 'https://static.readdy.ai/image/fd456b0d540e8f517714341c204b3126/e378a74dd87d9665caa97b92822badb0.png',
      link: '/produit-ange-a-poser-modele-2',
      badge: 'Nouveau ✨',
      badgeColor: 'bg-[#C49B86] text-white',
      isNew: true
    },
    {
      id: 'ange-a-poser-modele-3',
      name: 'Ange à poser – 25 cm',
      price: 30,
      category: 'Collection de Noël',
      subcategory: 'Anges de Noël',
      description: 'Petit ange à poser réalisé en livre plié. Élégant, délicat, et fabriqué avec soin.',
      image: productImages['ange-a-poser-modele-3'] || 'https://static.readdy.ai/image/fd456b0d540e8f517714341c204b3126/2d5aac0616d1916844df3dca44dc7e88.png',
      link: '/produit-ange-a-poser-modele-3',
      badge: 'Nouveau ✨',
      badgeColor: 'bg-[#C49B86] text-white',
      isNew: true
    },
    {
      id: 'lot-3-anges-reve-doux',
      name: 'Lot de 3 Anges "Rêve Doux"',
      price: 10,
      image: productImages['lot-3-anges-reve-doux'] || 'https://public.readdy.ai/ai/img_res/b9123dc7-dcd6-4b4d-a813-250efe08a010.png',
      category: 'Collection de Noël',
      subcategory: 'Anges de Noël',
      description: 'Lot de 3 pièces artisanales identiques',
      link: '/produit-lot-3-anges-reve-doux'
    },
    {
      id: 'lot-3-anges-serenite',
      name: 'Lot de 3 Anges "Sérénité"',
      price: 10,
      image: productImages['lot-3-anges-serenite'] || 'https://static.readdy.ai/image/fd456b0d540e8f517714341c204b3126/7211e22fa083ad77f32c6ab4cf399d10.png',
      category: 'Collection de Noël',
      subcategory: 'Anges de Noël',
      description: 'Lot de 3 pièces artisanales identiques',
      link: '/produit-lot-3-anges-serenite'
    },
    {
      id: 'lot-3-anges-mixte',
      name: 'Lot "Les 3 Anges de Lolotte"',
      price: 10,
      image: productImages['lot-3-anges-mixte'] || 'https://public.readdy.ai/ai/img_res/6d413a58-1c50-400a-be34-b0910d597568.png',
      category: 'Collection de Noël',
      subcategory: 'Anges de Noël',
      description: 'Assortiment mixte des 3 modèles',
      link: '/produit-lot-3-anges-mixte'
    },

    // Bougies en livres pliés
    {
      id: 'bougie-avec-led-v2',
      name: 'Bougie en livre plié – Avec LED',
      price: 30,
      originalPrice: null,
      image: productImages['bougie-avec-led-v2'] || 'https://static.readdy.ai/image/fd456b0d540e8f517714341c204b3126/842983f7d1219d4e05abdf64e978f33f.png',
      category: 'Collection de Noël',
      subcategory: 'Bougies de Noël',
      description: "Bougie décorative réalisée à partir d'un véritable livre ancien plié à la main. Chaque pièce est unique et ornée d'un ruban délicat. Une bougie LED amovible est incluse pour une ambiance douce, chaleureuse et sans danger.",
      link: '/produit-bougie-livre-plie-avec-led-v2',
      badge: 'Avec LED ✨',
      badgeColor: 'bg-[#B97A72] text-white',
      isNew: true
    },
    {
      id: 'bougie-livre-plie-edition-noel',
      name: 'Bougie en livre plié – Édition Noël (pièce unique)',
      price: 30,
      image: productImages['bougie-livre-plie-edition-noel'] || 'https://static.readdy.ai/image/fd456b0d540e8f517714341c204b3126/bae52e4e35f4de05b3d8393c829e34dc.png',
      category: 'Collection de Noël',
      subcategory: 'Bougies de Noël',
      description: "Bougie décorative réalisée à partir d'un livre soigneusement plié à la main, transformé en un véritable objet de poésie. Ornée d'un ruban délicat et de petits strass décoratifs. Bougie LED incluse.",
      link: '/produit-bougie-livre-plie-edition-noel',
      badge: 'Pièce unique ✨',
      badgeColor: 'bg-[#C49B86] text-white',
      isNew: true
    },
    {
      id: 'bougie-livre-plie-dentelle-or',
      name: 'Bougie en livre plié – Dentelle & Or (pièce unique)',
      price: 30,
      image: productImages['bougie-livre-plie-dentelle-or'] || 'https://static.readdy.ai/image/fd456b0d540e8f517714341c204b3126/6e9d5c191a7055f5df6e32d34b25a09c.png',
      category: 'Collection de Noël',
      subcategory: 'Bougies de Noël',
      description: "Bougie décorative réalisée à partir d'un livre entièrement plié à la main, sublimée par une bande de dentelle délicate, un ruban aux motifs dorés et de petits ornements scintillants. Bougie LED incluse.",
      link: '/produit-bougie-livre-plie-dentelle-or',
      badge: 'Pièce unique ✨',
      badgeColor: 'bg-[#C49B86] text-white',
      isNew: true
    },

    // Chandeliers
    {
      id: 'chandelier-livre-plie-dentelle-perles',
      name: 'Chandelier en livre plié – Dentelle & Perles (pièce unique)',
      price: 30,
      image: productImages['chandelier-livre-plie-dentelle-perles'] || 'https://static.readdy.ai/image/fd456b0d540e8f517714341c204b3126/d529905dc80190c446bfa46703479507.png',
      category: 'Collection de Noël',
      subcategory: 'Chandelier',
      description: "Chandelier décoratif réalisé à partir d'un livre entièrement plié à la main, transformé en un élégant support pour bougie. Il est orné de dentelle délicate, de perles blanches et d'un ruban aux motifs dorés, pour un rendu raffiné et lumineux.",
      link: '/produit-chandelier-livre-plie-dentelle-perles',
      badge: 'Pièce unique ✨',
      badgeColor: 'bg-[#C49B86] text-white',
      isNew: true
    },
    {
      id: 'chandelier-livre-plie-blanc-perles',
      name: 'Chandelier en livre plié – Blanc & Perles (pièce unique)',
      price: 30,
      image: productImages['chandelier-livre-plie-blanc-perles'] || 'https://static.readdy.ai/image/fd456b0d540e8f517714341c204b3126/3ef1ee7de7e5e4757619c328190956c7.png',
      category: 'Collection de Noël',
      subcategory: 'Chandelier',
      description: "Chandelier décoratif en livre plié, sublimé par de la dentelle délicate, de petites perles nacrées et un ruban blanc pour une finition douce et raffinée.",
      link: '/produit-chandelier-livre-plie-blanc-perles',
      badge: 'Pièce unique ✨',
      badgeColor: 'bg-[#C49B86] text-white',
      isNew: true
    },

    // Sapins de Noël en livres
    {
      id: 'sapin-noel-livre-plie',
      name: 'Sapin de Noël en livre plié',
      price: 30,
      image: productImages['sapin-noel-livre-plie'] || 'https://static.readdy.ai/image/fd456b0d540e8f517714341c204b3126/1efa15a3cda270c684d125f4f4d3ec67.png',
      category: 'Collection de Noël',
      subcategory: 'Sapins de Noël en livres',
      description: "Sapin réalisé à partir de pages pliées d'un véritable livre. Chaque modèle est unique et décoré à la main. Deux versions disponibles : pompon rouge ou pompon blanc.",
      link: '/produit-sapin-noel-livre-plie',
      variants: [
        { id: 'pompon-rouge', name: 'Pompon rouge' },
        { id: 'pompon-blanc', name: 'Pompon blanc' }
      ]
    },
    {
      id: 'sapin-lumineux-dore',
      name: 'Sapin lumineux doré – Livre plié',
      price: 30,
      image: productImages['sapin-lumineux-dore'] || 'https://static.readdy.ai/image/fd456b0d540e8f517714341c204b3126/3eac711adf0e5d03db76dab7cee730d9.png',
      category: 'Collection de Noël',
      subcategory: 'Sapins de Noël en livres',
      description: "Sapin lumineux entièrement réalisé à la main en livre plié. Mini guirlande LED intégrée pour une lumière douce et chaleureuse. Décoré d'un ruban doré satiné.",
      link: '/produit-sapin-lumineux-dore',
      badge: 'Nouveau ✨',
      badgeColor: 'bg-[#C49B86] text-white',
      isNew: true
    },
    {
      id: 'sapin-or-lumieres',
      name: 'Sapin de Noël en livre plié – Or & Lumières',
      price: 30,
      image: productImages['sapin-or-lumieres'] || 'https://static.readdy.ai/image/fd456b0d540e8f517714341c204b3126/ea5fb8890e6d6037d2a61c848616b776.png',
      category: 'Collection de Noël',
      subcategory: 'Sapins de Noël en livres',
      description: "Sapin décoratif réalisé à partir d'un livre entièrement plié à la main, sublimé par un ruban doré, une boule décorative et une guirlande lumineuse intégrée.",
      link: '/produit-sapin-or-lumieres',
      badge: 'Pièce unique ✨',
      badgeColor: 'bg-[#C49B86] text-white',
      isNew: true
    },
    {
      id: 'sapin-flamme-doree-lumieres',
      name: 'Sapin de Noël en livre plié – Flamme Dorée & Lumières (pièce unique)',
      price: 30,
      image: productImages['sapin-flamme-doree-lumieres'] || 'https://static.readdy.ai/image/fd456b0d540e8f517714341c204b3126/a9e9180f83d58d660f566f5909876bf9.png',
      category: 'Collection de Noël',
      subcategory: 'Sapins de Noël en livres',
      description: "Sapin décoratif réalisé à partir d'un livre entièrement plié à la main, surmonté d'une flamme décorative dorée torsadée et sublimé par une guirlande lumineuse intégrée.",
      link: '/produit-sapin-flamme-doree-lumieres',
      badge: 'Pièce unique ✨',
      badgeColor: 'bg-[#C49B86] text-white',
      isNew: true
    },

    // Boules de Noël
    {
      id: 'boule-noel-argent-pailletee',
      name: 'Boule de Noël argent pailletée',
      price: 4,
      originalPrice: null,
      image: productImages['boule-noel-argent-pailletee'] || 'https://static.readdy.ai/image/fd456b0d540e8f517714341c204b3126/1953d1495cd5e8d1c233a6c576865ce6.png',
      category: 'Collection de Noël',
      subcategory: 'Boules de Noël',
      description: "Boule de Noël artisanale à suspendre ou à poser. Couleur : argent pailleté ✨ Chaque boule est réalisée à la main et peut légèrement varier selon l'inspiration.",
      link: '/produit-boule-noel-argent-pailletee',
      badge: 'Nouveau 💫',
      badgeColor: 'bg-[#FFF8F5] text-[#C49B86]',
      isNew: true
    },
    {
      id: 'boule-noel-pages-livre',
      name: 'Boule de Noël en pages de livre',
      price: 4.00,
      description: 'Boule de Noël entièrement faite main à partir de pages de livre recyclées. Pièce unique et poétique.',
      image: productImages['boule-noel-pages-livre'] || 'https://static.readdy.ai/image/fd456b0d540e8f517714341c204b3126/6c8ed57cc94baac9e7088a44ece36c1a.png',
      link: '/produit-boule-noel-pages-livre',
      category: 'Collection de Noël',
      subcategory: 'Boules de Noël',
      isNew: true,
      badge: 'Nouveau 🎄',
      badgeColor: 'bg-[#C49B86] text-white'
    },
    {
      id: 'boule-noel-livres-plies-bleu',
      name: 'Boule de Noël – Livres Pliés (Modèle Bleu)',
      price: 4.00,
      description: 'Boule de Noël faite main en pages de livre recyclées avec inserts bleus. Création artisanale unique.',
      image: productImages['boule-noel-livres-plies-bleu'] || 'https://static.readdy.ai/image/fd456b0d540e8f517714341c204b3126/7442d34a47b8c68f484018ca82e96617.png',
      link: '/produit-boule-noel-livres-plies-bleu',
      category: 'Collection de Noël',
      subcategory: 'Boules de Noël',
      isNew: true,
      badge: 'Nouveau 🎄',
      badgeColor: 'bg-[#7BA5C4] text-white'
    },
    {
      id: 'boule-noel-papier-rouge-naturel',
      name: 'Boule de Noël en papier – Rouge & Naturel (pièce unique)',
      price: 4.00,
      description: 'Boule de Noël artisanale en papier épais, tissu rouge à flocons et perles bois. Pièce unique.',
      image: productImages['boule-noel-papier-rouge-naturel'] || 'https://static.readdy.ai/image/fd456b0d540e8f517714341c204b3126/30d563562a39e73a79c52a699e03bf8e.png',
      link: '/produit-boule-noel-papier-rouge-naturel',
      category: 'Collection de Noël',
      subcategory: 'Boules de Noël',
      isNew: true,
      badge: 'Pièce unique ✨',
      badgeColor: 'bg-[#C49B86] text-white'
    },
    {
      id: 'boule-noel-papier-bleu-pois-dores',
      name: 'Boule de Noël en papier – Bleu & Pois Dorés (pièce unique)',
      price: 4.00,
      description: 'Boule de Noël artisanale en papier plié bleu profond avec pois dorés, perles bois et ruban nude. Pièce unique.',
      image: productImages['boule-noel-papier-bleu-pois-dores'] || 'https://static.readdy.ai/image/fd456b0d540e8f517714341c204b3126/29824ce96cbebe40df2b2f4435a18104.png',
      link: '/produit-boule-noel-papier-bleu-pois-dores',
      category: 'Collection de Noël',
      subcategory: 'Boules de Noël',
      isNew: true,
      badge: 'Pièce unique ✨',
      badgeColor: 'bg-[#7BA5C4] text-white'
    },
    {
      id: 'boule-noel-papier-gris-argent',
      name: 'Boule de Noël en papier – Doux Gris & Argent (pièce unique)',
      price: 4.00,
      description: 'Boule de Noël artisanale en papier plié gris et argent avec perles bois et ruban argenté. Style épuré et élégant. Pièce unique.',
      image: productImages['boule-noel-papier-gris-argent'] || 'https://static.readdy.ai/image/fd456b0d540e8f517714341c204b3126/9666bdfa71b5e42184d5a770c75e4383.png',
      link: '/produit-boule-noel-papier-gris-argent',
      category: 'Collection de Noël',
      subcategory: 'Boules de Noël',
      isNew: true,
      badge: 'Pièce unique ✨',
      badgeColor: 'bg-[#C6A996] text-white'
    },

    // Décoration murale / à poser
    {
      id: 'decoration-murale-livre-sculpte-noel',
      name: 'Décoration murale – Livre sculpté & composition de Noël',
      price: 30,
      image: productImages['decoration-murale-livre-sculpte-noel'] || 'https://static.readdy.ai/image/fd456b0d540e8f517714341c204b3126/f113da5799b09ca49f47ff5690a7dd9b.png',
      category: 'Collection de Noël',
      subcategory: 'Décoration murale / à poser',
      description: 'Pièce unique réalisée à partir d\'un livre ancien plié et décoré à la main. Composition de Noël : pommes de pin, feuillage, paillettes dorées et éléments naturels.',
      link: '/produit-decoration-murale-livre-sculpte-noel',
      badge: 'Pièce unique ✨',
      badgeColor: 'bg-[#C49B86] text-white',
      isNew: true
    },
    {
      id: 'decoration-murale-feuillage-ornements-noel',
      name: 'Décoration murale en livre plié – Feuillage & Ornements de Noël (pièce unique)',
      price: 30,
      image: productImages['decoration-murale-feuillage-ornements-noel'] || 'https://static.readdy.ai/image/fd456b0d540e8f517714341c204b3126/a60f77ede70772177c06cff5f92b04f3.png',
      category: 'Collection de Noël',
      subcategory: 'Décoration murale / à poser',
      description: 'Décoration murale artisanale en livre plié, sublimée par un assemblage raffiné de feuilles de houx, branches de pin, boules rouges et dorées, et ruban doré.',
      link: '/produit-decoration-murale-feuillage-ornements-noel',
      badge: 'Pièce unique ✨',
      badgeColor: 'bg-[#C49B86] text-white',
      isNew: true
    },

    // Cages à oiseaux
    {
      id: 'cage-oiseaux-oiseau-fleurs',
      name: 'Cage à oiseaux en livre plié – Oiseau & Fleurs',
      price: 30,
      image: productImages['cage-oiseaux-oiseau-fleurs'] || 'https://static.readdy.ai/image/fd456b0d540e8f517714341c204b3126/e2823c3ad6f294174173f1ac5054b0ff.png',
      category: 'Cages à oiseaux',
      subcategory: 'Cage à oiseaux',
      description: 'Cage décorative en livre plié à la main, ornée de fleurs en papier rouge et crème, avec un petit oiseau décoratif. Création unique et poétique.',
      link: '/produit-cage-oiseaux-oiseau-fleurs',
      badge: 'Pièce unique ✨',
      badgeColor: 'bg-[#C49B86] text-white',
      isNew: true
    },
    {
      id: 'cage-oiseaux-trio-oiseaux-fleur-rouge',
      name: 'Cage à oiseaux en livre plié – Trio d\'oiseaux & Fleur rouge',
      price: 30,
      image: productImages['cage-oiseaux-trio-oiseaux-fleur-rouge'] || 'https://static.readdy.ai/image/fd456b0d540e8f517714341c204b3126/a70bc768183598607c06cff5f92b04f3.png',
      category: 'Cages à oiseaux',
      subcategory: 'Cage à oiseaux',
      description: 'Cage décorative en livre plié, sublimée par une grande fleur rouge aux finitions dorées, feuilles vertes et trio d\'oiseaux jaunes et rouges. Pièce unique.',
      link: '/produit-cage-oiseaux-trio-oiseaux-fleur-rouge',
      badge: 'Pièce unique ✨',
      badgeColor: 'bg-[#C49B86] text-white',
      isNew: true
    },

    // Collection Saint-Valentin - Tous les produits ex-VIP sont maintenant publics
    {
      id: 'deux-coeurs-une-histoire',
      name: 'Deux cœurs, une histoire',
      price: 30,
      image: 'https://static.readdy.ai/image/fd456b0d540e8f517714341c204b3126/8306a3be3e6bcfd00f47354001f0efb5.png',
      category: 'Collection Saint-Valentin',
      subcategory: 'Symboles & spiritualité douce',
      description: 'Deux cœurs liés façonnés dans les pages d\'un même livre. Symbole d\'amour, complicité et fidélité. Pièce unique faite main.',
      link: '/produit-deux-coeurs-une-histoire',
      badge: 'Pièce unique 💕',
      badgeColor: 'bg-[#B97A72] text-white',
      featured: true
    },
    {
      id: 'coeur-blanc-livre-plie',
      name: 'Cœur blanc – Livre plié (pièce unique)',
      price: 30,
      image: productImages['coeur-blanc-livre-plie'] || 'https://static.readdy.ai/image/fd456b0d540e8f517714341c204b3126/8e52fd41d5707d37aab1d9cca97e0148.png',
      category: 'Collection Saint-Valentin',
      subcategory: 'Symboles & spiritualité douce',
      description: 'Un grand cœur sculpté dans les pages d\'un livre, pour offrir un message d\'amour tout en douceur. Pièce unique, faite main.',
      link: '/produit-coeur-blanc-livre-plie',
      badge: 'Pièce unique 💕',
      badgeColor: 'bg-[#B97A72] text-white',
      featured: true
    },
    {
      id: 'love-livre-plie',
      name: 'Livre plié "LOVE"',
      subtitle: 'Pièce unique',
      price: 30,
      image: productImages['love-livre-plie'] || 'https://static.readdy.ai/image/fd456b0d540e8f517714341c204b3126/98714a791e9c4a77f66af69f82562095.png',
      badge: 'Pièce unique 💕',
      badgeColor: 'bg-[#B97A72] text-white',
      category: 'Collection Saint-Valentin',
      subcategory: 'Symboles & spiritualité douce',
      description: 'Un livre ancien transformé en sculpture poétique : le mot LOVE apparaît en relief, accompagné de petites fleurs en papier. Une pièce unique, parfaite pour déclarer ou célébrer l\'amour.',
      link: '/produit-love-livre-plie',
      stock: 1
    },
    {
      id: 'quatre-coeurs-livre-plie',
      name: 'Livre plié "Quatre cœurs"',
      subtitle: '3 petits cœurs & 1 grand (Pièce unique)',
      price: 30,
      image: productImages['quatre-coeurs-livre-plie'] || 'https://static.readdy.ai/image/fd456b0d540e8f517714341c204b3126/0b9aeda089d6f184b1d55f54e41ff5cb.png',
      badge: 'Pièce unique 💕',
      badgeColor: 'bg-[#B97A72] text-white',
      category: 'Collection Saint-Valentin',
      subcategory: 'Symboles & spiritualité douce',
      description: 'Une création en livre plié représentant 4 cœurs en relief : trois petits à gauche et un grand à droite. Pièce unique, tendre et symbolique.',
      link: '/produit-quatre-coeurs-livre-plie',
      stock: 1
    },
    {
      id: 'coeur-ruban-rouge-livre-plie',
      name: 'Livre plié "Cœur ruban rouge"',
      subtitle: 'Pièce unique',
      price: 30,
      image: productImages['coeur-ruban-rouge-livre-plie'] || 'https://static.readdy.ai/image/fd456b0d540e8f517714341c204b3126/3fa376e8e8807f31eaa1afa0fce6cfd3.png',
      badge: 'Pièce unique 💕',
      badgeColor: 'bg-[#B97A72] text-white',
      category: 'Collection Saint-Valentin',
      subcategory: 'Symboles & spiritualité douce',
      description: 'Un cœur sculpté dans les pages d\'un livre, sublimé par un ruban rouge et une touche florale. Une pièce unique, romantique et symbolique.',
      link: '/produit-coeur-ruban-rouge-livre-plie',
      stock: 1
    },

    // Spirituel & Symbolique - Produit ex-VIP maintenant public
    {
      id: 'jesus-portrait-livre-plie',
      name: 'Jésus – Portrait en livre plié',
      price: 59,
      image: productImages['jesus-portrait-livre-plie'] || 'https://static.readdy.ai/image/fd456b0d540e8f517714341c204b3126/4dafc94ac260b99dcf978d618eb08d45.png',
      category: 'Spirituel & Symbolique',
      subcategory: 'Art sacré',
      description: 'Création artisanale en livre plié représentant le visage de Jésus. Une pièce unique, réalisée à la main à partir d\'un livre ancien, pour une décoration spirituelle pleine de présence et de poésie.',
      link: '/produit-jesus-portrait-livre-plie',
      badge: 'Pièce unique ✨',
      badgeColor: 'bg-[#C49B86] text-white',
      featured: true
    },

    // Collection Pâques & Printemps
    {
      id: 'lapin-livre-plie',
      name: 'Lapin en livre plié',
      price: 9,
      image: 'https://storage.readdy-site.link/project_files/fbdeed4b-ebea-4a2a-94ca-d34ec0e1d4bd/5de4d05e-0365-46d0-930a-dbad130340c7_lapin-oreille-ronde.png?v=7096e813016ba1c48644c2eaa91f9ba5',
      category: 'Pâques',
      subcategory: 'Pâques',
      description: 'Un petit lapin en livre plié, fait main, tout doux et poétique. Parfait pour une décoration de printemps ou de Pâques.',
      link: '/produit-lapin-livre-plie',
      badge: 'Pièce unique ✨',
      badgeColor: 'bg-[#C49B86] text-white',
      stock: 20
    },
    {
      id: 'oeuf-paques-livre-plie',
      name: 'Œuf de Pâques en livre plié (pièce unique)',
      price: 9,
      image: 'https://storage.readdy-site.link/project_files/fbdeed4b-ebea-4a2a-94ca-d34ec0e1d4bd/7ad78983-aefe-41f7-b4d3-88ca4307df6f_oeuf-de-paques.png?v=61fae6b07adf3462af91e7213e4412af',
      category: 'Pâques',
      subcategory: 'Pâques',
      description: 'Un petit œuf décoratif en livre plié, fait main, poétique et léger. Parfait pour une décoration de Pâques douce.',
      link: '/produit-oeuf-paques-livre-plie',
      badge: 'Pièce unique ✨',
      badgeColor: 'bg-[#C49B86] text-white',
      stock: 1
    },
    // Signes du Zodiaque
    {
      id: 'belier-livre-plie',
      name: 'Bélier \u2648\uFE0E – Livre plié (pièce unique)',
      price: 30,
      image: 'https://public.readdy.ai/ai/img_res/1c9232d58f6bf3309b6fef9b73e8e081.jpg',
      category: 'Signes du Zodiaque',
      subcategory: 'Signes du Zodiaque',
      description: 'Le symbole \u2648\uFE0E du Bélier sculpté dans les pages d\'un livre. Une pièce unique, minimaliste et élégante.',
      link: '/produit-belier-livre-plie',
      badge: 'Pièce unique',
      badgeColor: 'bg-[#C49B86] text-white',
      stock: 1
    },

    // Organiseurs & Porte-lettres
    {
      id: 'herisson-porte-lettres-livre-plie',
      name: 'Hérisson porte-lettres en livre plié (pièce unique)',
      price: 30,
      image: 'https://storage.readdy-site.link/project_files/fbdeed4b-ebea-4a2a-94ca-d34ec0e1d4bd/cc708602-b8d9-473a-a5fd-ad64f3c89a7c_herisson-porte-lettre.png?v=8a15e9c968ee51dc9ca42398efc2e833',
      category: 'Organiseur',
      subcategory: 'Porte-lettres en livre plié',
      description: 'Un petit hérisson en livre plié qui garde joliment votre courrier : une création artisanale utile, poétique et pleine de charme.',
      link: '/produit-herisson-porte-lettres-livre-plie',
      badge: 'Pièce unique ✨',
      badgeColor: 'bg-[#C49B86] text-white',
      stock: 1
    },
    // Vases & Bouquets
    {
      id: 'bouquet-eternel-livre-plie',
      name: 'Livre plié « Bouquet éternel » (pièce unique)',
      price: 30,
      image: 'https://storage.readdy-site.link/project_files/fbdeed4b-ebea-4a2a-94ca-d34ec0e1d4bd/74813c06-f7b3-4490-891c-e6d4bd3b29e7_Vase-en-livre-avec-roses.png?v=02ad9ca1b9be210449a60e78a21cb4b5',
      category: 'Printemps',
      subcategory: 'Vase & Bouquet',
      description: 'Un livre transformé en vase poétique, habillé d\'un bouquet de roses rouges et ivoire. Une pièce unique, douce et lumineuse.',
      link: '/produit-bouquet-eternel-livre-plie',
      badge: 'Pièce unique ✨',
      badgeColor: 'bg-[#C49B86] text-white',
      stock: 1
    }
  ];

  // Fonction pour obtenir l'image (personnalisée ou par défaut)
  const getDisplayImage = (product: any) => {
    return product.image;
  };

  // Nouvelles collections permanentes
  const permanentCollections = [
    'Cadeaux qui ont du sens',
    'Décoration poétique',
    'Symboles & spiritualité douce',
    'Créations en livres pliés',
    'Signes du Zodiaque',
    'Animaux'
  ];

  // Collections saisonnières
  const seasonalCollections = [
    'Collection de Noël',
    'Collection Saint-Valentin',
    'Pâques',
    'Printemps'
  ];

  const categories = [...permanentCollections, ...seasonalCollections, 'Cages à oiseaux', 'Personnalisations & Commandes sur mesure'];
  const noelSubcategories = ['Bonhommes de neige', 'Anges de Noël', 'Bougies de Noël', 'Chandelier', 'Sapins de Noël en livres', 'Boules de Noël', 'Décoration murale / à poser'];

  const [selectedCategory, setSelectedCategory] = useState('Cadeaux qui ont du sens');
  const [selectedSubcategory, setSelectedSubcategory] = useState('Anges de Noël');
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });

  // Filtrer les produits en avant-première pour le public
  // UTILISER getVisibleProducts comme source de vérité unique
  const publicProducts = getVisibleProducts(products);

  // DEBUG: Calculer les statistiques
  const debugInfo = showDebug ? {
    isPreviewActive: isExclusivePreviewActive(),
    endDate: new Date(EXCLUSIVE_PREVIEW_END),
    now: new Date(),
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    exclusiveProducts: products.filter(p => p.isExclusivePreview === true),
    totalProducts: products.length,
    visibleProducts: publicProducts.length,
    exclusiveFilteredOut: products.length - publicProducts.length,
    deuxCoeurs: {
      product: products.find(p => p.id === 'deux-coeurs-une-histoire'),
      visibleToPublic: isProductVisibleToPublic(products.find(p => p.id === 'deux-coeurs-une-histoire') || {} as any)
    },
    jesus: {
      product: products.find(p => p.id === 'jesus-portrait-livre-plie'),
      visibleToPublic: isProductVisibleToPublic(products.find(p => p.id === 'jesus-portrait-livre-plie') || {} as any)
    }
  } : null;

  // Fonction pour associer les produits aux collections permanentes
  const getProductsByPermanentCollection = (collection: string) => {
    // IMPORTANT: Appliquer le filtre EN PREMIER sur tous les produits
    const visibleProducts = getVisibleProducts(products);
    
    switch (collection) {
      case 'Cadeaux qui ont du sens':
        return visibleProducts.filter(p => 
          p.subcategory === 'Anges de Noël' || 
          p.subcategory === 'Bougies de Noël' ||
          p.subcategory === 'Décoration murale / à poser' ||
          p.category === 'Collection Saint-Valentin' ||
          p.subcategory === 'Symboles & spiritualité douce' ||
          p.category === 'Spirituel & Symbolique' ||
          p.subcategory === 'Porte-lettres en livre plié' ||
          p.subcategory === 'Vase & Bouquet' ||
          p.category === 'Signes du Zodiaque'
        );
      
      case 'Décoration poétique':
        return visibleProducts.filter(p => 
          p.subcategory === 'Chandelier' || 
          p.category === 'Cages à oiseaux' ||
          p.subcategory === 'Sapins de Noël en livres' ||
          p.subcategory === 'Boules de Noël' ||
          p.category === 'Pâques' ||
          p.category === 'Printemps' ||
          p.subcategory === 'Porte-lettres en livre plié' ||
          p.category === 'Signes du Zodiaque'
        );
      
      case 'Symboles & spiritualité douce':
        return visibleProducts.filter(p => 
          p.subcategory === 'Anges de Noël' ||
          p.category === 'Cages à oiseaux' ||
          p.subcategory === 'Symboles & spiritualité douce' ||
          p.category === 'Collection Saint-Valentin' ||
          p.category === 'Spirituel & Symbolique'
        );
      
      case 'Créations en livres pliés':
        return visibleProducts;

      case 'Signes du Zodiaque':
        return visibleProducts.filter(p => p.category === 'Signes du Zodiaque');

      case 'Animaux':
        return visibleProducts.filter(p => p.category === 'Organiseur' || p.subcategory === 'Porte-lettres en livre plié');
      
      default:
        return [];
    }
  };

  const getFilteredProducts = () => {
    if (selectedCategory === 'Personnalisations & Commandes sur mesure') {
      return [];
    }

    // Collections permanentes
    if (permanentCollections.includes(selectedCategory)) {
      return getProductsByPermanentCollection(selectedCategory);
    }
    
    if (selectedCategory === 'Cages à oiseaux') {
      return publicProducts.filter(product => product.category === 'Cages à oiseaux');
    }

    if (selectedCategory === 'Collection de Noël') {
      if (selectedSubcategory === 'Bonhommes de neige') {
        const bonhommesProducts = [
          {
            id: 'bonhomme-neige-livre-plie',
            name: 'Bonhomme de neige en livre plié',
            price: 30,
            category: 'Collection de Noël',
            subcategory: 'Bonhommes de neige',
            description: 'Le gardien des pages d\'hiver',
            image: productImages['bonhomme-neige-livre-plie'] || 'https://static.readdy.ai/image/fd456b0d540e8f517714341c204b3126/7b82c9e731354e91cd23cedb6f7953b8.png',
            link: '/produit-bonhomme-neige-livre-plie',
            badge: '❄️ Nouveau',
            badgeColor: 'bg-[#C49B86] text-white'
          },
          {
            id: 'bonhomme-neige-lumineux',
            name: 'Bonhomme de Neige Lumineux – Livre Plié',
            price: 30,
            category: 'Collection de Noël',
            subcategory: 'Bonhommes de neige',
            description: 'Bonhomme de neige lumineux avec guirlande LED intégrée, écharpe et bonnet décoratifs',
            image: productImages['bonhomme-neige-lumineux'] || 'https://static.readdy.ai/image/fd456b0d540e8f517714341c204b3126/53d762511ac970f9fd94e51d54770e93.png',
            link: '/produit-bonhomme-neige-lumineux',
            badge: '💡 Lumineux',
            badgeColor: 'bg-[#B97A72] text-white',
            isNew: true
          }
        ];
        return getVisibleProducts(bonhommesProducts);
      }

      return publicProducts.filter(product =>
        product.category === 'Collection de Noël' &&
        product.subcategory === selectedSubcategory
      );
    }

    // Collection Saint-Valentin
    if (selectedCategory === 'Collection Saint-Valentin') {
      return publicProducts.filter(product => product.category === 'Collection Saint-Valentin');
    }

    // Collection Pâques
    if (selectedCategory === 'Pâques') {
      return publicProducts.filter(product => product.category === 'Pâques');
    }

    // Collection Printemps
    if (selectedCategory === 'Printemps') {
      return publicProducts.filter(product => product.category === 'Printemps');
    }

    return [];
  };

  const filteredProducts = getFilteredProducts();

  const showNotification = (message: string, type: 'success' | 'error' = 'success') => {
    if (!isMountedRef.current) return;
    
    if (notificationTimeoutRef.current) {
      clearTimeout(notificationTimeoutRef.current);
      notificationTimeoutRef.current = null;
    }

    setNotification({
      show: true,
      message,
      type
    });

    notificationTimeoutRef.current = setTimeout(() => {
      if (isMountedRef.current) {
        setNotification(prev => ({ ...prev, show: false }));
      }
      notificationTimeoutRef.current = null;
    }, 3000);
  };

  const handleAddToCart = (product: any) => {
    if (!isMountedRef.current) return;
    
    try {
      addItem({
        id: product.id,
        name: product.name,
        price: product.price,
        image: product.image,
        description: product.description
      });

      showNotification(`${product.name} ajouté au panier !`, 'success');

    } catch (error) {
      console.error('Erreur lors de l\'ajout au panier:', error);
      showNotification('Erreur lors de l\'ajout au panier', 'error');
    }
  };

  // Composant ProductCard pour éviter les conflits de rendu
  const ProductCard = ({ product, index }: { product: any; index: number }) => {
    const displayImage = getDisplayImage(product);
    
    return (
      <article
        className="bg-[#FAF9F8] border-2 border-[#B97A72] rounded-lg overflow-hidden hover:shadow-xl transition-all group"
        data-product-shop
      >
        {/* Image produit */}
        <div className="aspect-square bg-[#F3D9D9] relative overflow-hidden">
          {!imagesLoaded ? (
            <div className="w-full h-full flex items-center justify-center">
              <i className="ri-loader-4-line text-4xl text-[#B97A72] animate-spin"></i>
            </div>
          ) : (
            <img
              src={displayImage}
              alt={product.name}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            />
          )}

          {/* Badge */}
          {product.badge && (
            <div className="absolute top-2 sm:top-3 left-2 sm:left-3">
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${product.badgeColor || 'bg-[#B97A72] text-white'}`}>
                {product.badge}
              </span>
            </div>
          )}

          {/* Badge Nouveau */}
          {product.isNew && (
            <div className="absolute top-2 sm:top-3 right-2 sm:right-3">
              <span className="bg-[#C49B86] text-white px-2 py-1 rounded-full text-xs font-medium">
                Nouveau
              </span>
            </div>
          )}
        </div>

        <div className="p-4 sm:p-6">
          <span className="text-xs sm:text-sm text-[#B97A72] font-medium" style={{ fontFamily: 'Playfair Display, serif' }}>
            {product.subcategory}
          </span>
          <h3 className="text-lg sm:text-xl font-semibold text-[#6B5E5A] mt-2 mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
            {product.name}
          </h3>
          <p className="text-[#6B5E5A] text-xs sm:text-sm mb-4 line-clamp-2" style={{ fontFamily: 'Poppins, sans-serif' }}>
            {product.description}
          </p>
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-2">
            <span className="text-xl sm:text-2xl font-bold text-[#B97A72]" style={{ fontFamily: 'Playfair Display, serif' }}>
              {product.price}€
            </span>
            <div className="flex space-x-2 w-full sm:w-auto">
              {product.link !== '#' && (
                <Link
                  to={product.link}
                  className="flex-1 sm:flex-none bg-transparent text-[#B97A72] px-3 py-2 rounded-full border-2 border-[#B97A72] hover:bg-[#B97A72] hover:text-white transition-colors whitespace-nowrap cursor-pointer text-xs sm:text-sm text-center"
                  style={{ fontFamily: 'Playfair Display, serif' }}
                >
                  Voir
                </Link>
              )}
              <button
                onClick={() => handleAddToCart(product)}
                className="flex-1 sm:flex-none bg-[#B97A72] text-white px-3 py-2 rounded-full hover:bg-[#C49B86] transition-colors whitespace-nowrap cursor-pointer text-xs sm:text-sm"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                <div className="w-3 h-3 sm:w-4 sm:h-4 flex items-center justify-center mr-1 inline-block">
                  <i className="ri-shopping-cart-line"></i>
                </div>
                Ajouter
              </button>
            </div>
          </div>
        </div>
      </article>
    );
  };

  // Fonction pour obtenir le titre et la description de la collection
  const getCollectionInfo = () => {
    if (selectedCategory === 'Cadeaux qui ont du sens') {
      return {
        title: '💝 Cadeaux qui ont du sens',
        description: 'Des créations artisanales chargées d\'émotion et de poésie. Anges, bougies et décorations murales pour offrir avec le cœur.'
      };
    }
    if (selectedCategory === 'Décoration poétique') {
      return {
        title: '✨ Décoration poétique',
        description: 'Chandeliers, cages à oiseaux, sapins lumineux et boules de Noël. Des objets décoratifs qui transforment votre intérieur en un lieu de douceur.'
      };
    }
    if (selectedCategory === 'Symboles & spiritualité douce') {
      return {
        title: '🕊️ Symboles & spiritualité douce',
        description: 'Anges gardiens, oiseaux et symboles de lumière. Des créations qui élèvent l\'âme et apportent sérénité.'
      };
    }
    if (selectedCategory === 'Créationsions en livres pliés') {
      return {
        title: '📖 Créations en livres pliés',
        description: 'Toutes mes créations artisanales réalisées à partir de livresanciens pliés à la main. Chaque pièce est unique et raconte une histoire.'
      };
    }
    if (selectedCategory === 'Signes du Zodiaque') {
      return {
        title: 'Signes du Zodiaque',
        description: 'Créations artisanales en livres pliés inspirées des signes astrologiques.'
      };
    }
    if (selectedCategory === 'Animaux') {
      return {
        title: 'Animaux',
        description: 'Créations artisanales en livres pliés inspirées des animaux, poétiques et pleines de charme.'
      };
    }
    if (selectedCategory === 'Collection Saint-Valentin') {
      return {
        title: '💕 Collection Saint-Valentin',
        description: 'Créations romantiques pour célébrer l\'amour. Des symboles poétiques façonnés à la main pour offrir avec le cœur.'
      };
    }
    if (selectedCategory === 'Pâques') {
      return {
        title: '🐣 Pâques',
        description: 'Lapins et œufs en livres pliés, faits main avec douceur. Des créations poétiques pour célébrer Pâques.'
      };
    }
    if (selectedCategory === 'Printemps') {
      return {
        title: '🌸 Printemps',
        description: 'Des créations légères et fleuries pour accueillir le printemps. Réalisées à la main à partir de livres recyclés.'
      };
    }
    return { title: '', description: '' };
  };

  return (
    <div className="min-h-screen bg-[#F8EDEA]">
      {/* DEBUG PANEL - Visible uniquement avec ?debug=1 */}
      {showDebug && debugInfo && (
        <div className="bg-yellow-100 border-4 border-yellow-500 p-6 m-4 rounded-lg font-mono text-sm">
          <h2 className="text-2xl font-bold mb-4 text-red-600">🔍 DEBUG MODE</h2>
          
          <div className="mb-4 p-4 bg-white rounded">
            <h3 className="font-bold text-lg mb-2">📅 État de l'avant-première</h3>
            <p><strong>isExclusivePreviewActive:</strong> <span className={debugInfo.isPreviewActive ? 'text-green-600' : 'text-red-600'}>{debugInfo.isPreviewActive ? 'true' : 'false'}</span></p>
            <p><strong>exclusivePreviewEndDate:</strong> {debugInfo.endDate.toLocaleString('fr-FR', { timeZone: debugInfo.timezone, dateStyle: 'full', timeStyle: 'long' })}</p>
            <p><strong>now:</strong> {debugInfo.now.toLocaleString('fr-FR', { timeZone: debugInfo.timezone, dateStyle: 'full', timeStyle: 'long' })}</p>
            <p><strong>timezone:</strong> {debugInfo.timezone}</p>
          </div>

          <div className="mb-4 p-4 bg-white rounded">
            <h3 className="font-bold text-lg mb-2">📊 Compteurs</h3>
            <p><strong>totalProducts:</strong> {debugInfo.totalProducts}</p>
            <p><strong>visibleProducts:</strong> {debugInfo.visibleProducts}</p>
            <p><strong>exclusiveFilteredOut:</strong> {debugInfo.exclusiveFilteredOut}</p>
          </div>

          <div className="mb-4 p-4 bg-white rounded">
            <h3 className="font-bold text-lg mb-2">🎯 Produits marqués isExclusivePreview: true</h3>
            {debugInfo.exclusiveProducts.length === 0 ? (
              <p className="text-red-600">❌ Aucun produit trouvé avec isExclusivePreview: true</p>
            ) : (
              <ul className="list-disc pl-5">
                {debugInfo.exclusiveProducts.map(p => (
                  <li key={p.id}>
                    <strong>{p.name}</strong> (slug: {p.id})
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="mb-4 p-4 bg-white rounded">
            <h3 className="font-bold text-lg mb-2">💕 "Deux cœurs, une histoire"</h3>
            {debugInfo.deuxCoeurs.product ? (
              <>
                <p><strong>ID:</strong> {debugInfo.deuxCoeurs.product.id}</p>
                <p><strong>Nom:</strong> {debugInfo.deuxCoeurs.product.name}</p>
                <p><strong>isExclusivePreview:</strong> {debugInfo.deuxCoeurs.product.isExclusivePreview ? 'true' : 'false'}</p>
                <p><strong>visibleToPublic:</strong> <span className={debugInfo.deuxCoeurs.visibleToPublic ? 'text-red-600' : 'text-green-600'}>{debugInfo.deuxCoeurs.visibleToPublic ? 'true ❌ (PROBLÈME!)' : 'false ✅'}</span></p>
              </>
            ) : (
              <p className="text-red-600">❌ Produit non trouvé</p>
            )}
          </div>

          <div className="mb-4 p-4 bg-white rounded">
            <h3 className="font-bold text-lg mb-2">✝️ "Jésus – Portrait en livre plié"</h3>
            {debugInfo.jesus.product ? (
              <>
                <p><strong>ID:</strong> {debugInfo.jesus.product.id}</p>
                <p><strong>Nom:</strong> {debugInfo.jesus.product.name}</p>
                <p><strong>isExclusivePreview:</strong> {debugInfo.jesus.product.isExclusivePreview ? 'true' : 'false'}</p>
                <p><strong>visibleToPublic:</strong> <span className={debugInfo.jesus.visibleToPublic ? 'text-red-600' : 'text-green-600'}>{debugInfo.jesus.visibleToPublic ? 'true ❌ (PROBLÈME!)' : 'false ✅'}</span></p>
              </>
            ) : (
              <p className="text-red-600">❌ Produit non trouvé</p>
            )}
          </div>
        </div>
      )}

      {/* Encart Prix unique */}
      <div className="bg-[#FAF9F8] border-b border-[#EBD2CF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 text-center">
          <p className="text-sm sm:text-base text-[#6B5E5A]" style={{ fontFamily: 'Poppins, sans-serif' }}>
            <span className="text-[#B97A72] font-semibold">Prix unique 30€</span> sur la majorité des créations ✨ <span className="text-[#9B8B87]">(hors mini formats et pièces spéciales)</span>
          </p>
        </div>
      </div>

      {/* Hero Section */}
      <header className="bg-[#FAF9F8] py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#B97A72] mb-4 sm:mb-6" style={{ fontFamily: 'Allura, cursive' }}>
            Notre Boutique
          </h1>
          
          {/* Phrase de positionnement */}
          <p className="text-lg sm:text-xl lg:text-2xl text-[#6B5E5A] mb-6 sm:mb-8 px-4 font-medium" style={{ fontFamily: 'Playfair Display, serif' }}>
            Les Carnets de Lolotte proposent des créations artisanales en livres pliés, entre décoration poétique et cadeaux chargés de sens.
          </p>
          
          <div className="bg-[#F8EDEA] border-2 border-[#B97A72] rounded-lg p-4 sm:p-6 max-w-4xl mx-auto mb-6 sm:mb-8">
            <p className="text-base sm:text-lg lg:text-xl text-[#6B5E5A] flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-3" style={{ fontFamily: 'Poppins, sans-serif' }}>
              <i className="ri-heart-line text-5xl sm:text-7xl text-[#B97A72]"></i>
              Découvrez mes créations artisanales en livres pliés – faites main avec amour 💕
            </p>
          </div>
        </div>
      </header>

      {/* Section Nouveautés */}
      <NewArrivalsSection />

      {/* Filtres par catégorie principale */}
      <nav className="py-6 sm:py-8 bg-[#EBD2CF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Collections permanentes */}
          <div className="mb-6">
            <h3 className="text-center text-lg font-semibold text-[#6B5E5A] mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
              Collections permanentes
            </h3>
            <div className="flex flex-wrap justify-center gap-3 sm:gap-4">
              {permanentCollections.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-4 sm:px-6 py-2 sm:py-3 rounded-full font-medium transition-colors whitespace-nowrap border-2 text-sm sm:text-base ${
                    selectedCategory === category
                      ? 'bg-[#B97A72] text-white border-[#B97A72]'
                      : 'bg-[#FAF9F8] text-[#6B5E5A] border-[#B97A72] hover:bg-[#F8EDEA]'
                  }`}
                  style={{ fontFamily: 'Playfair Display, serif' }}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>

          {/* Collections saisonnières */}
          <div className="mb-4">
            <h3 className="text-center text-lg font-semibold text-[#6B5E5A] mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
              Collections saisonnières
            </h3>
            <div className="flex flex-wrap justify-center gap-3 sm:gap-4 mb-4 sm:mb-6">
              {seasonalCollections.map((category) => (
                <button
                  key={category}
                  onClick={() => {
                    setSelectedCategory(category);
                    if (category === 'Collection de Noël') {
                      setSelectedSubcategory('Anges de Noël');
                    }
                  }}
                  className={`px-4 sm:px-6 py-2 sm:py-3 rounded-full font-medium transition-colors whitespace-nowrap border-2 text-sm sm:text-base ${
                    selectedCategory === category
                      ? 'bg-[#B97A72] text-white border-[#B97A72]'
                      : 'bg-[#FAF9F8] text-[#6B5E5A] border-[#B97A72] hover:bg-[#F8EDEA]'
                  }`}
                  style={{ fontFamily: 'Playfair Display, serif' }}
                >
                  {category}
                </button>
              ))}
              <button
                onClick={() => setSelectedCategory('Cages à oiseaux')}
                className={`px-4 sm:px-6 py-2 sm:py-3 rounded-full font-medium transition-colors whitespace-nowrap border-2 text-sm sm:text-base ${
                  selectedCategory === 'Cages à oiseaux'
                    ? 'bg-[#B97A72] text-white border-[#B97A72]'
                    : 'bg-[#FAF9F8] text-[#6B5E5A] border-[#B97A72] hover:bg-[#F8EDEA]'
                }`}
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                Cages à oiseaux
              </button>
              <button
                onClick={() => setSelectedCategory('Personnalisations & Commandes sur mesure')}
                className={`px-4 sm:px-6 py-2 sm:py-3 rounded-full font-medium transition-colors whitespace-nowrap border-2 text-sm sm:text-base ${
                  selectedCategory === 'Personnalisations & Commandes sur mesure'
                    ? 'bg-[#B97A72] text-white border-[#B97A72]'
                    : 'bg-[#FAF9F8] text-[#6B5E5A] border-[#B97A72] hover:bg-[#F8EDEA]'
                }`}
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                Personnalisations & Commandes sur mesure
              </button>
            </div>
          </div>

          {/* Sous-catégories pour Collection de Noël */}
          {selectedCategory === 'Collection de Noël' && (
            <div className="flex flex-wrap justify-center gap-2 sm:gap-3">
              {noelSubcategories.map((subcategory) => (
                <button
                  key={subcategory}
                  onClick={() => setSelectedSubcategory(subcategory)}
                  className={`px-3 sm:px-4 py-2 rounded-full text-xs sm:text-sm font-medium transition-colors whitespace-nowrap border ${
                    selectedSubcategory === subcategory
                      ? 'bg-[#C49B86] text-white border-[#C49B86]'
                      : 'bg-white text-[#6B5E5A] border-[#C49B86] hover:bg-[#F8EDEA]'
                  }`}
                  style={{ fontFamily: 'Poppins, sans-serif' }}
                >
                  {subcategory}
                </button>
              ))}
            </div>
          )}
        </div>
      </nav>

      {/* Grille de produits */}
      <main className="py-12 sm:py-16" data-product-shop>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {selectedCategory === 'Personnalisations & Commandes sur mesure' ? (
            <section className="text-center py-12 sm:py-16">
              <div className="bg-[#FAF9F8] border-2 border-[#B97A72] rounded-2xl p-8 sm:p-12 max-w-3xl mx-auto shadow-lg">
                <div className="w-16 h-16 sm:w-20 sm:h-20 flex items-center justify-center mx-auto mb-4 sm:mb-6">
                  <i className="ri-palette-line text-5xl sm:text-7xl text-[#B97A72]"></i>
                </div>
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#C49B86] mb-3 sm:mb-4" style={{ fontFamily: 'Allura, cursive' }}>
                  Créations personnalisées
                </h2>
                <p className="text-lg sm:text-xl text-[#6B5E5A] mb-3 sm:mb-4 italic leading-relaxed px-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Parce qu'un livre peut raconter bien plus qu'une histoire…
                </p>
                <p className="text-base sm:text-lg text-[#6B5E5A] mb-4 sm:mb-6 leading-relaxed px-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  Offrez (ou offrez‑vous) une création entièrement personnalisée : un livre plié unique, orné d'une photo, d'un prénom, d'un mot doux ou d'un symbole particulier.
                </p>
                <div className="bg-white border-2 border-[#B97A72] rounded-lg p-4 sm:p-6 mb-6 sm:mb-8 inline-block">
                  <p className="text-xs sm:text-sm text-[#6B5E5A] mb-2" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    À partir de
                  </p>
                  <p className="text-3xl sm:text-4xl font-bold text-[#B97A72]" style={{ fontFamily: 'Allura, cursive' }}>
                    70 €
                  </p>
                </div>
                <a
                  href="/personnalisations"
                  className="bg-[#B97A72] text-white px-6 sm:px-10 py-3 sm:py-4 rounded-full hover:bg-[#C49B86] transition-colors whitespace-nowrap cursor-pointer inline-block text-base sm:text-lg font-medium"
                  style={{ fontFamily: 'Allura, cursive' }}
                >
                  <i className="ri-heart-line mr-2"></i>
                  Découvrir les créations sur mesure
                </a>
              </div>
            </section>
          ) : selectedCategory === 'Collection Saint-Valentin' ? (
            <>
              <div className="text-center mb-8 sm:mb-12">
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#C49B86] mb-3 sm:mb-4" style={{ fontFamily: 'Allura, cursive' }}>
                  💕 Collection Saint-Valentin
                </h2>
                <p className="text-base sm:text-lg lg:text-xl text-[#6B5E5A] max-w-3xl mx-auto px-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Créations romantiques pour célébrer l'amour. Des symboles poétiques façonnés à la main pour offrir avec le cœur.
                </p>
              </div>

              <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
                {filteredProducts.map((product, index) => (
                  <ProductCard 
                    key={`saint-valentin-${product.id}-${index}`} 
                    product={product} 
                    index={index}
                  />
                ))}
              </section>
            </>
          ) : selectedCategory === 'Printemps' ? (
            <>
              <div className="text-center mb-8 sm:mb-12">
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#C49B86] mb-3 sm:mb-4" style={{ fontFamily: 'Allura, cursive' }}>
                  🐰 Printemps
                </h2>
                <p className="text-base sm:text-lg lg:text-xl text-[#6B5E5A] max-w-3xl mx-auto px-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Des créations douces et poétiques pour accueillir le printemps. Réalisées à la main à partir de livres recyclés.
                </p>
              </div>

              <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
                {filteredProducts.map((product, index) => (
                  <ProductCard
                    key={`paques-${product.id}-${index}`}
                    product={product}
                    index={index}
                  />
                ))}
              </section>
            </>
          ) : selectedCategory === 'Pâques' ? (
            <>
              <div className="text-center mb-8 sm:mb-12">
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#C49B86] mb-3 sm:mb-4" style={{ fontFamily: 'Allura, cursive' }}>
                  🐰 Pâques
                </h2>
                <p className="text-base sm:text-lg lg:text-xl text-[#6B5E5A] max-w-3xl mx-auto px-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Des créations douces et poétiques pour accueillir le printemps et fêter Pâques. Réalisées à la main à partir de livres recyclés.
                </p>
              </div>

              <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
                {filteredProducts.map((product, index) => (
                  <ProductCard 
                    key={`pâques-${product.id}-${index}`} 
                    product={product} 
                    index={index}
                  />
                ))}
              </section>
            </>
          ) : selectedCategory === 'Cages à oiseaux' ? (
            <>
              <div className="text-center mb-8 sm:mb-12">
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#C49B86] mb-3 sm:mb-4" style={{ fontFamily: 'Allura, cursive' }}>
                  🎀 Cages à oiseaux
                </h2>
                <p className="text-base sm:text-lg lg:text-xl text-[#6B5E5A] max-w-3xl mx-auto px-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Créations délicates et poétiques en livres pliés, ornées de fleurs et d'oiseaux décoratifs.
                </p>
              </div>

              <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
                {filteredProducts.map((product, index) => (
                  <ProductCard 
                    key={`cage-${product.id}-${index}`} 
                    product={product} 
                    index={index}
                  />
                ))}
              </section>
            </>
          ) : permanentCollections.includes(selectedCategory) ? (
            <>
              <div className="text-center mb-8 sm:mb-12">
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#C49B86] mb-3 sm:mb-4" style={{ fontFamily: 'Allura, cursive' }}>
                  {getCollectionInfo().title}
                </h2>
                <p className="text-base sm:text-lg lg:text-xl text-[#6B5E5A] max-w-3xl mx-auto px-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                  {getCollectionInfo().description}
                </p>
              </div>

              <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
                {filteredProducts.map((product, index) => (
                  <ProductCard 
                    key={`permanent-${selectedCategory}-${product.id}-${index}`} 
                    product={product} 
                    index={index}
                  />
                ))}
              </section>
            </>
          ) : (
            <>
              <div className="text-center mb-8 sm:mb-12">
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#C49B86] mb-3 sm:mb-4" style={{ fontFamily: 'Allura, cursive' }}>
                  {selectedSubcategory === 'Bonhommes de neige' ? '❄️ Bonhommes de neige' :
                   selectedSubcategory === 'Anges de Noël' ? '👼 Anges de Noël' :
                   selectedSubcategory === 'Bougies de Noël' ? '🕯️ Bougies de Noël' :
                   selectedSubcategory === 'Chandelier' ? '🕯️ Chandelier' :
                   selectedSubcategory === 'Sapins de Noël en livres' ? '🌲 Sapins de Noël en livres' :
                   selectedSubcategory === 'Boules de Noël' ? '✨ Boules de Noël' :
                   selectedSubcategory}
                </h2>
                <p className="text-base sm:text-lg lg:text-xl text-[#6B5E5A] max-w-3xl mx-auto px-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                  {selectedSubcategory === 'Bonhommes de neige' ? (
                    <>Des gardiens des pages d'hiver, pleins de charme et de poésie ❄️</>
                  ) : selectedSubcategory === 'Anges de Noël' ? (
                    <>Petites créations tendres faites main à partir de livresanciens.</>
                  ) : selectedSubcategory === 'Bougies de Noël' ? (
                    <>Bougie décorative réalisée à partir d'un véritable livre ancien plié à la main. Chaque pièce est unique et ornée d'un ruban délicat. Une bougie LED amovible est incluse pour une ambiance douce, chaleureuse et sans danger.</>
                  ) : selectedSubcategory === 'Chandelier' ? (
                    <>Chandelier décoratif en livre plié, transformé en élégant support pour bougie.<br />Création unique et raffinée pour une ambiance chaleureuse.</>
                  ) : selectedSubcategory === 'Sapins de Noël en livres' ? (
                    <>Sapin réalisé à partir de pages pliées d'un véritable livre. Chaque modèle est unique et décoré à la main. Deux versions disponibles : pompon rouge ou pompon blanc.</>
                  ) : selectedSubcategory === 'Boules de Noël' ? (
                    <>Boules de Noël artisanales ✨<br />Créée uniques en papier plié pour décorer votre sapin avec poésie et originalité.</>
                  ) : (
                    <>Collection de Noël 🎁<br />Des créations artisanales uniques, pleines de douceur, à offrir ou à s'offrir.</>
                  )}
                </p>
              </div>

              <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
                {filteredProducts.map((product, index) => (
                  <ProductCard 
                    key={`noel-${selectedSubcategory}-${product.id}-${index}`} 
                    product={product} 
                    index={index}
                  />
                ))}
              </section>
            </>
          )}
        </div>
      </main>

      {/* Notification toast */}
      {notification.show && (
        <div 
          className={`fixed top-20 right-4 z-50 px-6 py-3 rounded-lg shadow-lg transition-all duration-300 ${
            notification.type === 'success' 
              ? 'bg-green-500 text-white' 
              : 'bg-red-500 text-white'
          }`}
          style={{ 
            transform: notification.show ? 'translateX(0)' : 'translateX(100%)',
            opacity: notification.show ? 1 : 0
          }}
        >
          <div className="flex items-center space-x-2">
            <i className={`${notification.type === 'success' ? 'ri-check-line' : 'ri-error-warning-line'}`}></i>
            <span style={{ fontFamily: 'Poppins, sans-serif' }}>{notification.message}</span>
          </div>
        </div>
      )}

    </div>
  );
}
